import { HttpRequest } from "./utils/HttpRequest";
import { Vehicle as VehicleModel } from "./models/Vehicle";

/** `fileType` válidos para `uploadVehicle`. `"photo"` ya no se acepta — el backend responde
 * "El archivo no es válido." */
export type VehicleFileType =
  | "insurancePhoto"
  | "technicalReviewPhoto1"
  | "technicalReviewPhoto2";

/** Fila de `findVehicles`: sólo patente y marca, sin tope, orden alfabético por patente. */
export interface FindVehicleResult {
  id: string;
  plate: string;
  /** Cadena vacía si el vehículo no tiene marca cargada. */
  brand: string;
}

/** Cuerpo de `addVehicle`. Sólo `token`, `user` (persona) y `plate` son obligatorios; el resto se
 * manda cuando el formulario lo tiene. `brandId` / `insuranceCompanyId` reemplazan a `brand` /
 * `insuranceCompany` (texto libre, legacy), que se siguen mandando como etiqueta. */
export interface AddVehicleParams {
  token: string;
  /** User ID de la persona atendida. */
  user?: string;
  /** @deprecated Nombre viejo de `user`. Se sigue aceptando pero si vienen los dos gana `user`. */
  id?: string;
  plate: string;
  /** Carrocería del catálogo `vehicleTypes`. */
  vehicleType?: string;
  brandId?: string;
  brand?: string;
  model?: string;
  color?: string;
  technicalReviewExpirationDate?: string;
  insuranceCompanyId?: string;
  insuranceCompany?: string;
  insurancePolicy?: string;
  insuranceExpirationDate?: string;
}

/** Cuerpo de `updateVehicle`. Ojo con `id`: acá es el id del **vehículo**, no el del usuario como en
 * `addVehicle`. La patente no se puede cambiar por este endpoint: no la acepta. */
export interface UpdateVehicleParams {
  token: string;
  id: string;
  vehicleType?: string;
  brandId?: string;
  brand?: string;
  model?: string;
  color?: string;
  technicalReviewExpirationDate?: string;
  insuranceCompanyId?: string;
  insuranceCompany?: string;
  insurancePolicy?: string;
  insuranceExpirationDate?: string;
}

export default class Vehicle {
  httpReq = new HttpRequest();
  //Services
  updateVehicle(params: UpdateVehicleParams): Promise<any> {
    const { token, id, ...fields } = params;
    const body: Record<string, any> = { token, id };
    // A diferencia de `addVehicle`, acá nunca se manda un valor vacío: sería pisar con "" un dato
    // que el vehículo ya tiene (p. ej. modelo y color, que el formulario de transportistas ni
    // muestra).
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        body[key] = value;
      }
    });
    return this.httpReq.post(`ws-security/updateVehicle`, body);
  }

  addVehicle(params: AddVehicleParams): Promise<any> {
    const {
      token,
      user,
      id,
      plate,
      brand,
      model,
      color,
      insuranceExpirationDate,
      technicalReviewExpirationDate,
      ...schemaFields
    } = params;

    // Las claves de siempre viajan siempre, aunque vengan vacías: es el cuerpo que ya manda el
    // alta de vehículos de los demás flujos y no queremos moverlo. `user` es la persona; `id` sólo
    // viaja si no hay `user` (alias legacy, deprecado).
    const body: Record<string, any> = {
      token,
      ...(user ? { user } : { id }),
      plate,
      brand: brand ?? "",
      model: model ?? "",
      color: color ?? "",
      insuranceExpirationDate: insuranceExpirationDate ?? "",
      technicalReviewExpirationDate,
    };

    // Las del esquema nuevo sólo cuando el formulario las completó.
    Object.entries(schemaFields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        body[key] = value;
      }
    });

    return this.httpReq.post(`ws-security/addVehicle`, body);
  }

  /** @deprecated Congelado por el backend: devuelve todos los vehículos de la persona. Reemplazado
   * por `findVehicles` + `selectVehicle` en el flujo de ingreso; sigue vivo sólo para el modo
   * egreso y las versiones de app sin actualizar. */
  getVehicles(token: string, id: string): Promise<any> {
    return this.httpReq.post(`ws-security/getVehicles`, {
      token,
      id,
    });
  }

  /** Busca vehículos de la persona por patente. Mínimo 3 caracteres útiles, obligatorio; busca por
   * "empieza con". Devuelve sólo patente y marca — los datos completos llegan con `selectVehicle`. */
  findVehicles(
    token: string,
    user: string,
    plate: string
  ): Promise<{ status: string; data: FindVehicleResult[] }> {
    return this.httpReq.post(`ws-security/findVehicles`, {
      token,
      user,
      plate,
    });
  }

  /** Obligatoria en cada selección (no sólo la primera): copia al complejo lo que el usuario tenga
   * cargado en su app y habilita leer/editar sus fotos. El `id` que devuelve puede ser distinto del
   * buscado — hay que usar ése para `updateVehicle`/`uploadVehicle`/el evento. */
  selectVehicle(
    token: string,
    user: string,
    id: string
  ): Promise<{ status: string; data: VehicleModel }> {
    return this.httpReq.post(`ws-security/selectVehicle`, {
      token,
      user,
      id,
    });
  }

  uploadImageVehicle(token: string, file: string, id: string) {
    return this.httpReq.post(`ws-security/uploadVehicle`, {
      token,
      file,
      id,
    });
  }

  getVehicleBrands(token: string, search?: string): Promise<any> {
    const body: { token: string; search?: string } = { token };
    if (search) {
      body.search = search;
    }
    return this.httpReq.post(`ws-security/getVehicleBrands`, body);
  }

  getInsuranceCompanies(token: string, search?: string): Promise<any> {
    const body: { token: string; search?: string } = { token };
    if (search) {
      body.search = search;
    }
    return this.httpReq.post(`ws-security/getInsuranceCompanies`, body);
  }
}
